import { useMemo, useEffect, useState } from "react";

import { ACTIVITY_TYPES } from "Constants/constants";
import useDecodedParams from "Hooks/useDecodedParams";
import useEnvironments from "Hooks/useEnvironments";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { getSelectors } from "Reducers/activity";
import { environmentLoadingSelector } from "Reducers/environment";
import {
  deploymentSizingApiEnabledSelector,
  isLoadingSelector,
  loadDeploymentNext,
  deploymentStatusCodesSelector,
  allResourcesHaveProfileSizeSelector
} from "Reducers/environment/deployment";
import { gitProjectSelector } from "Reducers/project/project";
import { filterRecentActivities } from "src/organization/common/containers/ActivitySection/ActivitySection";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import type { Environment } from "@packages/client";

/**
 *  The configure resources sticker/banner should only be visible under two circumstances
 * 1. When a user initializes the resources of a project with the CLI via (git push upsun <branch> -o resources.init=manual) or (git push origin <branch> -o resources.init=manual)
 * 2. When any of the resources (webapps, workers, services) don't have a saved "profile_size"
 */

export const useShowResourcesSticker = () => {
  const [canRedeploy, setCanRedeploy] = useState(false);
  const [isReloadingDeployment, setIsReloadingDeployment] = useState(false);

  const dispatch = useAppDispatch();

  const { projectId, organizationId, environmentId, integrationId } =
    useDecodedParams<{
      projectId: string;
      organizationId: string;
      environmentId: string;
      integrationId: string;
    }>();

  const allEnvironments = useEnvironments(projectId);
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const envId = environmentId || project?.default_branch;
  const currentEnvironment = envId && allEnvironments?.[envId];
  const hasManuallyInitializedResources = !(currentEnvironment as Environment)
    ?.has_deployment;

  const { inProgress, pending, isLoading } = getSelectors("project", {
    projectId,
    organizationId,
    environmentId: envId,
    integrationId
  });
  const { current: currentCode, next: nextCode } = useAppSelector(state =>
    deploymentStatusCodesSelector(state, {
      organizationId,
      projectId,
      environmentId: envId!
    })
  );
  const isSizingApiEnabled = useAppSelector(state =>
    deploymentSizingApiEnabledSelector(state, {
      organizationId,
      projectId,
      environmentId: envId
    })
  );
  const isEnvironmentLoading = useAppSelector(state =>
    environmentLoadingSelector(state, { environmentId: envId, projectId })
  );
  const isLoadingActivities = useAppSelector(isLoading);
  const pendingActivity = useAppSelector(pending);
  const isDeploymentLoading = useAppSelector(isLoadingSelector);
  const recentPendingActivities = useMemo(
    () => filterRecentActivities(pendingActivity),
    [pendingActivity]
  );

  const inProgressActivity = useAppSelector(inProgress);
  const recentInprogressActivities = useMemo(
    () => filterRecentActivities(inProgressActivity),
    [inProgressActivity]
  );

  const hideResourceStickerForActivities = useMemo(() => {
    return [...recentInprogressActivities, ...recentPendingActivities].find(
      activity =>
        (
          [
            ACTIVITY_TYPES.ENVIRONMENT.BRANCH,
            ACTIVITY_TYPES.ENVIRONMENT.RESOURCES.UPDATE,
            ACTIVITY_TYPES.ENVIRONMENT.PUSH
          ] as string[]
        ).includes(activity.type)
    );
  }, [recentInprogressActivities, recentPendingActivities]);

  const manualResourcesAndStatus =
    hasManuallyInitializedResources && currentCode === 404 && nextCode === 404;

  const allResourcesHaveProfileSize = useAppSelector(state =>
    allResourcesHaveProfileSizeSelector(state, {
      organizationId,
      projectId,
      environmentId: envId
    })
  );

  const resourcesAreIncomplete =
    typeof allResourcesHaveProfileSize !== "undefined" &&
    !allResourcesHaveProfileSize;

  const showResourcesSticker =
    isSizingApiEnabled &&
    resourcesAreIncomplete &&
    !hideResourceStickerForActivities &&
    !isLoadingActivities &&
    !isDeploymentLoading &&
    !isReloadingDeployment &&
    !isEnvironmentLoading;

  /**
   * In the scenario where a user manually initializes the resources, upon completion, the deployments/next in the redux store is holding an older version of values.
   *
   * The solution is to set a few conditions for which once met will redeploy deployments/next and retrieve the most recent values, preventing the configure resources sticker from ever displaying.
   */

  useEffect(() => {
    if (
      manualResourcesAndStatus &&
      hideResourceStickerForActivities &&
      !canRedeploy
    ) {
      setCanRedeploy(true);
    }
  }, [manualResourcesAndStatus, hideResourceStickerForActivities, canRedeploy]);

  useEffect(() => {
    const doDeploy = async () => {
      try {
        setIsReloadingDeployment(true);
        setCanRedeploy(false);
        await dispatch(
          loadDeploymentNext({
            organizationId,
            projectId,
            environmentId: envId!
          })
        );
      } finally {
        setIsReloadingDeployment(false);
      }
    };

    if (
      canRedeploy &&
      !hideResourceStickerForActivities &&
      manualResourcesAndStatus &&
      !isLoadingActivities &&
      !isDeploymentLoading
    ) {
      doDeploy();
    }
  }, [
    dispatch,
    organizationId,
    projectId,
    envId,
    hideResourceStickerForActivities,
    manualResourcesAndStatus,
    canRedeploy,
    isLoadingActivities,
    isDeploymentLoading
  ]);

  return !!showResourcesSticker;
};
